import { Box } from "@mui/material";
import strings from "global/constants/StringConstants";
import { maskedContent } from "helpers/maskedContent";
import { formatFileSize, safeFileName } from "../ContractCompareTypesAndValidation";
import styles from "../ContractCompare.styles";
import t from "../ContractCompare.tokens";
import { AlertCircleGlyph, CheckGlyph, CloseGlyph } from "./ContractCompareIcons";
import KindTile from "./KindTile";

interface FileChipProps {
  file: File;
  /** Unique on the page (one chip per side): the name element's id, which labels the chip and describes Remove. */
  nameId: string;
  /** The file was refused (browser check or server): the chip turns red and says "not accepted". */
  invalid?: boolean;
  onRemove: () => void;
  /** The element that holds this file's error text, so a screen reader hears it when it reaches the remove button. */
  describedBy?: string;
}

/**
 * The chosen file: its kind, name, size and state, with a remove button of 44 px. A file name is customer data:
 * it is masked from session recording and autocapture (D41, D59) and shown as text only. The remove button's own
 * label is generic ("Remove file"): analytics tools record a button's label, and the name would travel with it. The
 * name reaches a screen reader through `aria-describedby` instead, together with the file's error text.
 */
const FileChip = ({ file, nameId, invalid = false, onRemove, describedBy }: FileChipProps) => {
  const page = strings.contractCompare.page;
  return (
    <Box sx={styles.chip(invalid)} role="group" aria-labelledby={nameId} {...maskedContent}>
      <KindTile fileName={file.name} />
      <Box sx={styles.rowText}>
        <Box component="span" id={nameId} dir="auto" sx={styles.fileName}>
          {safeFileName(file.name)}
        </Box>
        <Box component="span" sx={styles.fileMeta}>
          {formatFileSize(file.size)}
          {page.separator}
          <Box component="span" sx={styles.chipStatus(invalid)}>
            {invalid ? (
              <AlertCircleGlyph sx={{ width: 14, height: 14 }} />
            ) : (
              <CheckGlyph sx={{ width: 14, height: 14 }} />
            )}
            {invalid ? page.fileNotAccepted : page.fileReady}
          </Box>
        </Box>
      </Box>
      <Box
        component="button"
        type="button"
        sx={styles.chipRemove}
        onClick={onRemove}
        aria-label={page.removeFile}
        aria-describedby={describedBy ? `${nameId} ${describedBy}` : nameId}
      >
        <CloseGlyph sx={{ width: t.chip.removeIcon, height: t.chip.removeIcon }} />
      </Box>
    </Box>
  );
};

export default FileChip;
